<template>
  <div>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          elevation="13"
          color="secondary"
          class="ma-1 accent lighten-5"
          dark
          v-bind="attrs"
          v-on="on"
          @click="openNotes(userId)"
        >
          <v-icon>mdi-notebook</v-icon>
        </v-btn>
      </template>
      <span>Notes</span>
    </v-tooltip>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          elevation="13"
          color="exercise"
          class="ma-1 accent lighten-5"
          dark
          v-bind="attrs"
          v-on="on"
          @click="showLog('activity', username, clientName, userId)"
        >
          <v-icon>fitness_center</v-icon>
          <div v-if="activityLoggedToday" class="exercise-dot"></div>
        </v-btn>
      </template>
      <span>{{ activityLoggedToday || "Activity Log" }}</span>
    </v-tooltip>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          elevation="13"
          color="nutrition"
          class="ma-1 accent lighten-5"
          dark
          v-bind="attrs"
          v-on="on"
          @click="showLog('nutrition', username, clientName, userId)"
        >
          <v-icon>local_dining</v-icon>
        </v-btn>
      </template>
      <span>Nutrition Log</span>
    </v-tooltip>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          elevation="13"
          color="selection"
          class="ma-1 accent lighten-5"
          dark
          v-bind="attrs"
          v-on="on"
          @click="showLog('reflection', username, clientName, userId)"
        >
          <v-icon>stars</v-icon>
        </v-btn>
      </template>
      <span>Reflection Log</span>
    </v-tooltip>

    <v-dialog v-model="modals.notes" class="limited-height" width="900" transition="dialog-bottom-transition">
      <user-notes
        v-if="modals.notes"
        :userId="selectedUserId"
        :coachId="coachId"
        @problem="emitProblem()"
        @close="closeNotes"
      />
    </v-dialog>
    <v-dialog v-if="modals.log" v-model="modals.log" transition="dialog-bottom-transition">
      <v-row class="d-flex">
        <v-col v-if="modals.trainingLog" :cols="cols.left">
          <v-card class="overflow-hidden">
            <v-toolbar dark color="exercise">
              <v-toolbar-title>Activity Log for {{ selectedFullname }}</v-toolbar-title>
              <v-spacer />
              <v-btn v-if="cols.left != 10 && modals.nutritionLog" small icon dark class="mr-2" @click="expand(true)">
                <v-icon size="20">mdi-arrow-expand-right</v-icon>
              </v-btn>
              <v-btn v-if="cols.left != 6 && modals.nutritionLog" small icon dark @click="resetCols()">
                <v-icon>mdi-format-horizontal-align-center</v-icon>
              </v-btn>
              <v-btn icon dark @click="closeLog('activity')">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <div class="overflow-width" :class="{ half: cols.left == 6 }">
              <weekly-training-log
                ref="activity"
                :userId="selectedUserId"
                :coaching-chat-id="coachingChatId"
                @update-week="updateWeek('nutrition', $event)"
              />
            </div>
          </v-card>
        </v-col>
        <v-col v-if="modals.nutritionLog" :cols="cols.right">
          <v-card class="overflow-hidden">
            <v-toolbar dark color="nutrition darken-1">
              <v-toolbar-title>Nutrition Log for {{ selectedFullname }}</v-toolbar-title>
              <v-spacer />
              <v-btn v-if="cols.right != 10 && modals.trainingLog" small icon dark class="mr-2" @click="expand(false)">
                <v-icon size="20">mdi-arrow-expand-left</v-icon>
              </v-btn>
              <v-btn v-if="cols.right != 6 && modals.trainingLog" small icon dark @click="resetCols()">
                <v-icon>mdi-format-horizontal-align-center</v-icon>
              </v-btn>
              <v-btn icon dark @click="closeLog('nutrition')">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <div class="overflow-width" :class="{ half: cols.left == 6 }">
              <weekly-nutrition-log
                ref="nutrition"
                :userId="selectedUserId"
                @update-week="updateWeek('activity', $event)"
              />
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-dialog>
    <v-dialog v-model="modals.reflectionLog" width="900" transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Reflection Log for {{ selectedFullname }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="modals.reflectionLog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <reflection :userId="selectedUserId"></reflection>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UserNotes from "@/components/dashboard/components/notes/UserNotes";
import WeeklyTrainingLog from "@/components/training_log/WeeklyTrainingLog";
import WeeklyNutritionLog from "@/components/nutrition/WeeklyNutritionLog";
import Reflection from "@/components/client/blocks/reflection/Reflection";
import { CLIENT_INFORMATION_QUERY } from "@/constants/graphql/client/client";

export default {
  name: "ToolButtons",
  components: {
    UserNotes,
    WeeklyTrainingLog,
    WeeklyNutritionLog,
    Reflection,
  },
  props: {
    coachId: {
      type: String,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
    username: {
      type: String,
      required: true,
    },
    clientName: {
      type: String,
      required: true,
    },
    activityLoggedToday: {
      type: String,
      required: false,
    },
    injectedData: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      modals: {
        notes: false,
        log: false,
        trainingLog: true,
        nutritionLog: true,
        reflectionLog: false,
      },
      cols: {
        left: 6,
        right: 6,
      },
      selectedUserId: null,
      selectedFullname: null,
      selectedUsername: null,
      coachingChatId: null,
    };
  },
  mounted() {
    if (this.injectedData) {
      this.clientInformation = this.injectedData;
      this.coachingChatId = this.clientInformation?.userProfile?.coachingChat?.id;
    }
  },
  methods: {
    resetCols() {
      this.cols.left = 6;
      this.cols.right = 6;
    },
    expand(left) {
      this.cols.left = left ? 10 : 2;
      this.cols.right = left ? 2 : 10;
    },
    updateWeek(log, interval) {
      switch (log) {
        case "activity":
          this.$refs.activity.updateWeek(interval);
          break;
        case "nutrition":
          this.$refs.nutrition.updateWeek(interval);
          break;
      }
    },
    closeLog(log) {
      switch (log) {
        case "activity":
          if (!this.modals.nutritionLog) {
            this.modals.log = false;
            this.modals.nutritionLog = true;
            this.modals.trainingLog = true;
            this.resetCols();
          } else {
            this.modals.trainingLog = false;
            this.cols.right = 12;
          }
          break;
        case "nutrition":
          if (!this.modals.trainingLog) {
            this.modals.log = false;
            this.modals.trainingLog = true;
            this.modals.nutritionLog = true;
            this.resetCols();
          } else {
            this.modals.nutritionLog = false;
            this.cols.left = 12;
          }
          break;
      }
    },
    showLog(log, username, fullname, userId) {
      this.setSelected(username, fullname, userId);

      if (log == "reflection") {
        return (this.modals.reflectionLog = true);
      }

      switch (log) {
        case "activity":
          this.cols.left = 10;
          this.cols.right = 2;
          break;
        case "nutrition":
          this.cols.left = 2;
          this.cols.right = 10;
          break;
      }
      this.modals.trainingLog = true;
      this.modals.nutritionLog = true;
      this.modals.log = true;
    },
    openNotes(userId) {
      this.selectedUserId = userId;
      this.modals.notes = true;
    },
    closeNotes() {
      this.modals.notes = false;
    },
    setSelected(username, fullname, userId) {
      this.selectedFullname = fullname;
      this.selectedUsername = username;
      this.selectedUserId = userId;
    },
  },
  apollo: {
    clientInformation: {
      query: CLIENT_INFORMATION_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.coachingChatId = response.data?.clientInformation?.user?.userProfile?.coachingChat?.id;
        }
      },
      skip() {
        return this.injectedData;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.overflow-width:not(.half) {
  width: 78vw;
}
</style>
